import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import PageHeader from '@/components/layout/PageHeader';
import AppSelect from '@/components/ui/app-select';
import { can, get } from '@/utils/service';
import DashboardOverview from './Overview';
import type { OrganizationDashboard } from './types';

const periods = [
  ['this_month', 'This month'], ['last_month', 'Last month'], ['last_30_days', 'Last 30 days'],
  ['this_quarter', 'This quarter'], ['this_year', 'This year'],
];

export function Dashboard() {
  const [period, setPeriod] = useState('this_month');
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState<OrganizationDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const actions = [
    { label: 'Add new lead', to: '/sales/leads', permission: 'leads.create' },
    { label: 'View deals', to: '/projects/all', permission: 'deals.view' },
    { label: 'Invoices', to: '/invoices', permission: 'invoices.view' },
    { label: 'New expense', to: '/expenses/new', permission: 'expenses.create' },
    { label: 'Calendar', to: '/calendar', permission: 'activities.view' },
    { label: 'Manage team', to: '/users', permission: 'members.invite' },
  ].filter((action) => can(action.permission));

  useEffect(() => {
    const refresh = () => { setData(null); setRevision((value) => value + 1); };
    window.addEventListener('session-changed', refresh);
    return () => window.removeEventListener('session-changed', refresh);
  }, []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    setData(null);
    get<{ data: OrganizationDashboard }>('/dashboard', { period })
      .then((response) => { if (live) setData(response.data); })
      .catch(() => { if (live) setError(true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [period, revision]);

  return <div className="space-y-4">
    <PageHeader title="Dashboard" description={data?.scope === 'mine' ? 'Your business overview' : 'Your organization at a glance'}>
      <AppSelect value={period} onChange={(event) => setPeriod(event.target.value)} aria-label="Dashboard period" className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs">
        {periods.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </AppSelect>
      <button type="button" onClick={() => setRevision((value) => value + 1)} disabled={loading} aria-label="Refresh dashboard" className="rounded-full border border-gray-200 bg-white p-2 text-gray-500 disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></button>
      {can('leads.create') && <Link to="/sales/leads" className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-3 py-2 text-xs font-medium text-white"><Plus className="h-3.5 w-3.5" />Add lead</Link>}
    </PageHeader>
    {loading ? <div role="status" aria-label="Loading dashboard" className="space-y-4 animate-pulse"><div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">{[0, 1, 2, 3].map((key) => <div key={key} className="h-40 rounded-2xl bg-gray-100" />)}</div><div className="h-80 rounded-2xl bg-gray-100" /></div>
      : error ? <div role="alert" className="rounded-2xl border border-gray-200 bg-white p-8 text-center"><p className="text-sm text-gray-600">Unable to load your dashboard.</p><button type="button" onClick={() => setRevision((value) => value + 1)} className="mt-3 rounded-full bg-gray-900 px-4 py-2 text-xs text-white">Try again</button></div>
      : data && <DashboardOverview data={data} actions={actions} />}
  </div>;
}
